/**
 * LOOKBOOK — <lookbook-hotspots> and the kit-card panel trigger.
 * docs/07 §7, §11 · docs/08 §8 · docs/35 B5, B9.
 *
 * IIFE-wrapped: docs/38 §7 puts TWO lookbook sections on the homepage — the
 * kit cards and the shoppable image — so this file's tag is emitted twice and
 * the browser executes it twice. A top-level `class` re-declared that way
 * throws at parse time (docs/54 §5.6).
 */
(function () {
  if (customElements.get('lookbook-hotspots')) return;

  /* ---------------------------------------------------------------------- *
   * Kit cards — docs/07 §7.2
   *
   * The panel is a <modal-dialog>, so everything that makes it a dialog is
   * Step 3.7's. This is only the trigger wiring, delegated from the document
   * so a section re-render in the theme editor does not need re-binding.
   * ---------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-kit-open]');
    if (!trigger) return;

    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel || typeof panel.show !== 'function') return;

    event.preventDefault();
    panel.show(trigger);
  });

  /* ---------------------------------------------------------------------- *
   * <lookbook-hotspots> — docs/07 §11, docs/08 §12
   *
   * docs/07 §11 and docs/08 §12 both mark the interaction "Inferred: hover
   * reveals the product card on desktop; tap reveals it on mobile" (B14).
   * Neither was observed, and hover-only would repeat S10 — a touchscreen
   * laptop reaches this band on width alone.
   *
   * So CLICK is the interaction at every band, on a real button, and hover is
   * a pointer-only assist layered on top in CSS. That is the same resolution
   * B1 got for the mega menus at Step 4.8: hover alone has no keyboard
   * affordance at all.
   * ---------------------------------------------------------------------- */
  class LookbookHotspots extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeyDown.bind(this));

      // Closing on outside click has to be on the document — the point is
      // the click that is NOT in this element.
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get toggles() {
      return [...this.querySelectorAll('[data-hotspot-toggle]')];
    }

    onClick(event) {
      const toggle = event.target.closest('[data-hotspot-toggle]');
      if (!toggle) return;
      event.preventDefault();

      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      // One at a time. Six hotspots on a 218px-tall image at 390px
      // (docs/08 §12) means two open cards would overlap each other.
      this.closeAll();
      if (!isOpen) this.open(toggle);
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      const open = this.toggles.find((t) => t.getAttribute('aria-expanded') === 'true');
      if (!open) return;
      this.closeAll();
      // Escape must land focus somewhere deliberate, not on <body>.
      open.focus();
    }

    open(toggle) {
      const card = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!card) return;
      toggle.setAttribute('aria-expanded', 'true');
      // `hidden` rather than a class: it keeps the card out of the
      // accessibility tree AND out of the tab order while closed, which a
      // visibility class alone does not do.
      card.hidden = false;
    }

    closeAll() {
      this.toggles.forEach((toggle) => {
        if (toggle.getAttribute('aria-expanded') !== 'true') return;
        toggle.setAttribute('aria-expanded', 'false');
        const card = document.getElementById(toggle.getAttribute('aria-controls'));
        if (card) card.hidden = true;
      });
    }
  }

  customElements.define('lookbook-hotspots', LookbookHotspots);
})();
